import {
  dashboardRowsQueryOptions,
  dashboardUnassignedActionsQueryOptions,
} from "@/lib/data/react-query/matters/dashboard-tables";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  VITE_FAST_API_URL,
  VITE_MATTER_STREAM_URL,
} from "@/lib/data/static/importEnv";
import type {
  DashboardRowsFilters,
  DashboardUnassignedActionsFilters,
  MatterDashboardTable,
} from "@/lib/types/MatterDashboardTable";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

const protocol = "lawworks-api.phase-dashboard.v1";
const minimumRefreshMs = 5_000;
const fallbackPollMs = 15_000;
// Reconcile an open stream at the server's fallback interval so a missed hint
// cannot leave a visible table stale until the user refreshes the page.
const safetyRefreshMs = fallbackPollMs;

type Status = "connecting" | "ready" | "reconnecting" | "degraded";

interface ChangeFrame {
  type: "phase_dashboard.changed";
  cursor: string;
  sequence: number;
  phaseIds: string[];
  tableIds: string[];
  matterIds: string[];
  cellsOnly: boolean;
  catalogChanged: boolean;
  configurationChanged: boolean;
  myTeamsChanged: boolean;
  allTables: boolean;
  minimumRefreshIntervalSeconds: number;
  fallbackPollIntervalSeconds: number;
}

interface Options {
  firmId: string;
  userId: string;
  activeTabId: string | undefined;
  tables: MatterDashboardTable[];
  enabled?: boolean;
}

const isChange = (value: unknown): value is ChangeFrame => {
  if (!value || typeof value !== "object") return false;
  const frame = value as Partial<ChangeFrame>;
  return (
    frame.type === "phase_dashboard.changed" &&
    typeof frame.cursor === "string" &&
    typeof frame.sequence === "number" &&
    Array.isArray(frame.phaseIds) &&
    Array.isArray(frame.tableIds) &&
    typeof frame.allTables === "boolean"
  );
};

const streamUrl = (after?: string) => {
  const base: unknown = VITE_MATTER_STREAM_URL || VITE_FAST_API_URL;
  if (typeof base !== "string" || !base)
    throw new Error("Missing dashboard stream URL");
  const url = new URL("/matters/v2/dashboard/stream", base);
  url.protocol =
    url.protocol === "https:" || url.protocol === "wss:" ? "wss:" : "ws:";
  if (after) url.searchParams.set("after", after);
  return url.toString();
};

/** Maintains one firm stream while this dashboard surface is mounted and visible. */
export const useDashboardTableLiveUpdates = ({
  firmId,
  userId,
  activeTabId,
  tables,
  enabled = true,
}: Options) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>("connecting");
  const [afterSequence, setAfterSequence] = useState(0);
  const visibleRef = useRef({ activeTabId, tables });
  visibleRef.current = { activeTabId, tables };
  const reconnectRef = useRef<() => void>(() => {});
  const refreshVisibleRef = useRef<() => void>(() => {});
  const visibleTableIds = tables.map((table) => table.id).join("\0");

  useEffect(() => {
    if (!enabled || !firmId || !userId) return;
    let disposed = false;
    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    let pollTimer: number | undefined;
    let refreshTimer: number | undefined;
    let reconnectAttempts = 0;
    let appliedCursor: string | undefined;
    let pendingCursor: string | undefined;
    let latestSequence = 0;
    let pendingSequence = 0;
    let publishedSequence = 0;
    let lastRefreshAt = 0;
    let refreshing = false;
    let connectedOnce = false;
    let streamGeneration = 0;
    let nextRefreshDelay = minimumRefreshMs;
    let forceRefresh = false;
    const isDisposed = () => disposed;
    const isHidden = () => document.hidden;
    const dirtyTables = new Set<string>();

    const visibleTables = () => visibleRef.current.tables;
    const activeRowsKey = (tableId: string) => [
      queryKeys.matterDashboardRows,
      firmId,
      userId,
      tableId,
    ];
    const activeQueueKey = (tableId: string) => [
      queryKeys.matterDashboardUnassignedActions,
      firmId,
      userId,
      tableId,
    ];
    const refreshLists = async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardTabs, firmId, userId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardTables, firmId, userId],
        }),
      ]);
    };
    const refreshCatalog = async () => {
      await Promise.all([
        refreshLists(),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardPhaseOptions, firmId, userId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardActionOptions, firmId, userId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardDataItemOptions, firmId, userId],
        }),
      ]);
    };
    const resetStreamSequence = () => {
      streamGeneration += 1;
      appliedCursor = undefined;
      pendingCursor = undefined;
      latestSequence = 0;
      pendingSequence = 0;
      publishedSequence = 0;
      connectedOnce = false;
      setAfterSequence(0);
    };
    const markAllVisibleDirty = () => {
      visibleTables().forEach((table) => dirtyTables.add(table.id));
    };

    const scheduleRefresh = (immediate = false) => {
      if (
        disposed ||
        document.hidden ||
        refreshing ||
        refreshTimer !== undefined
      )
        return;
      const delay = immediate
        ? 0
        : Math.max(200, lastRefreshAt + nextRefreshDelay - Date.now());
      refreshTimer = window.setTimeout(() => void refreshDirty(), delay);
    };

    const refreshDirty = async () => {
      refreshTimer = undefined;
      if (disposed || document.hidden || refreshing) return;
      const activeIds = new Set(visibleTables().map((table) => table.id));
      const ids = [...dirtyTables].filter((id) => activeIds.has(id));
      if (!ids.length) return;
      ids.forEach((id) => dirtyTables.delete(id));
      const capturedSequence = pendingSequence;
      const capturedCursor = pendingCursor;
      const capturedGeneration = streamGeneration;
      refreshing = true;
      lastRefreshAt = Date.now();
      try {
        // Fetch the next sequence before publishing it. Publishing on receipt
        // would trigger a new query key and bypass the refresh throttle.
        for (let index = 0; index < ids.length; index += 3) {
          await Promise.all(
            ids.slice(index, index + 3).map(async (id) => {
              const table = visibleTables().find((item) => item.id === id);
              const isQueue = table?.type === "unassigned_actions";
              const queries = queryClient.getQueryCache().findAll({
                queryKey: isQueue ? activeQueueKey(id) : activeRowsKey(id),
                type: "active",
              });
              await Promise.all(
                queries.map((query) => {
                  if (isQueue) {
                    const filters = query
                      .queryKey[4] as DashboardUnassignedActionsFilters;
                    return queryClient.fetchQuery({
                      ...dashboardUnassignedActionsQueryOptions(
                        firmId,
                        userId,
                        id,
                        { ...filters, afterSequence: capturedSequence },
                      ),
                      staleTime: 0,
                      retry: false,
                    });
                  }
                  const filters = query.queryKey[4] as DashboardRowsFilters;
                  return queryClient.fetchQuery({
                    ...dashboardRowsQueryOptions(firmId, userId, id, {
                      ...filters,
                      afterSequence: capturedSequence,
                    }),
                    staleTime: 0,
                    retry: false,
                  });
                }),
              );
            }),
          );
        }
        if (isDisposed() || capturedGeneration !== streamGeneration) return;
        if (capturedSequence > publishedSequence) {
          publishedSequence = capturedSequence;
          setAfterSequence(capturedSequence);
        }
        nextRefreshDelay = minimumRefreshMs + Math.random() * 1_000;
        if (capturedSequence === pendingSequence && capturedCursor) {
          appliedCursor = capturedCursor;
        }
      } catch (error) {
        ids.forEach((id) => dirtyTables.add(id));
        if (capturedGeneration !== streamGeneration) return;
        if (
          isAxiosError<{ error?: { code?: string } }>(error) &&
          error.response?.status === 400 &&
          error.response.data.error?.code === "dashboard_cursor_future"
        ) {
          // A reset stream cannot satisfy the previous stream's sequence.
          resetStreamSequence();
          socket?.close(1000, "Dashboard sequence reset");
        } else {
          nextRefreshDelay = Math.min(fallbackPollMs, nextRefreshDelay * 2);
        }
      } finally {
        refreshing = false;
        if (dirtyTables.size) scheduleRefresh();
      }
    };

    const schedulePoll = () => {
      window.clearTimeout(pollTimer);
      if (disposed || document.hidden || socket?.readyState === WebSocket.OPEN)
        return;
      pollTimer = window.setTimeout(
        () => {
          markAllVisibleDirty();
          scheduleRefresh(true);
          schedulePoll();
        },
        fallbackPollMs + Math.random() * 1_000,
      );
    };

    const connect = async () => {
      if (disposed || document.hidden) return;
      window.clearTimeout(reconnectTimer);
      setStatus(connectedOnce ? "reconnecting" : "connecting");
      try {
        const session = await fetchAuthSession(
          forceRefresh ? { forceRefresh: true } : undefined,
        );
        const token = session.tokens?.accessToken.toString();
        if (!token) throw new Error("Missing Cognito access token");
        if (isDisposed() || isHidden()) return;
        const current = new WebSocket(streamUrl(appliedCursor), [
          protocol,
          token,
        ]);
        socket = current;
        current.onopen = () => {
          if (current.protocol !== protocol)
            current.close(1000, "Unexpected stream protocol");
        };
        current.onmessage = (event) => {
          if (disposed || current !== socket) return;
          let frame: unknown;
          try {
            frame = JSON.parse(String(event.data));
          } catch {
            current.close();
            return;
          }
          if (!frame || typeof frame !== "object") return;
          const value = frame as Record<string, unknown>;
          if (
            value.type === "stream.ready" &&
            typeof value.headSequence === "number"
          ) {
            const firstConnection = !connectedOnce;
            connectedOnce = true;
            reconnectAttempts = 0;
            forceRefresh = false;
            // Replay frames can be at or below the current head. Keep them
            // eligible so their configuration/catalog flags are processed.
            latestSequence =
              firstConnection || !appliedCursor
                ? Math.max(latestSequence, value.headSequence)
                : publishedSequence;
            pendingSequence = Math.max(pendingSequence, value.headSequence);
            if (firstConnection) {
              publishedSequence = pendingSequence;
              setAfterSequence(pendingSequence);
            } else {
              // Reconnect snapshots respect the refresh throttle.
              markAllVisibleDirty();
              scheduleRefresh();
            }
            setStatus("ready");
            window.clearTimeout(pollTimer);
            return;
          }
          if (isChange(value)) {
            if (value.sequence <= latestSequence) return;
            latestSequence = value.sequence;
            pendingSequence = Math.max(pendingSequence, value.sequence);
            pendingCursor = value.cursor;
            const broad =
              value.allTables ||
              value.catalogChanged ||
              value.configurationChanged ||
              value.myTeamsChanged;
            if (broad) markAllVisibleDirty();
            else {
              const phaseIds = new Set(value.phaseIds);
              visibleTables().forEach((table) => {
                if (
                  table.type === "unassigned_actions" ||
                  value.tableIds.includes(table.id) ||
                  table.phaseIds.some((id) => phaseIds.has(id))
                ) {
                  dirtyTables.add(table.id);
                }
              });
            }
            if (value.catalogChanged) void refreshCatalog();
            if (value.configurationChanged || value.myTeamsChanged)
              void refreshLists();
            scheduleRefresh();
            return;
          }
          if (value.type === "stream.resync_required") {
            resetStreamSequence();
            markAllVisibleDirty();
            scheduleRefresh();
            current.close();
            return;
          }
          if (value.type === "stream.shutdown") current.close();
        };
        current.onerror = () => {
          current.close();
        };
        current.onclose = (event) => {
          if (disposed || current !== socket) return;
          socket = undefined;
          setStatus("degraded");
          if (event.code === 1008) {
            forceRefresh = true;
            resetStreamSequence();
            void refreshLists();
          }
          schedulePoll();
          if (!document.hidden) {
            const delay =
              Math.random() * Math.min(1_000 * 2 ** reconnectAttempts, 30_000);
            reconnectAttempts += 1;
            reconnectTimer = window.setTimeout(() => void connect(), delay);
          }
        };
      } catch {
        if (isDisposed()) return;
        setStatus("degraded");
        schedulePoll();
        reconnectTimer = window.setTimeout(
          () => void connect(),
          Math.min(1_000 * 2 ** reconnectAttempts++, 30_000),
        );
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        socket?.close(1000, "Dashboard hidden");
        socket = undefined;
        window.clearTimeout(reconnectTimer);
        window.clearTimeout(pollTimer);
        window.clearTimeout(refreshTimer);
        refreshTimer = undefined;
      } else {
        markAllVisibleDirty();
        scheduleRefresh();
        void connect();
      }
    };
    const onFocus = () => {
      if (!document.hidden) {
        markAllVisibleDirty();
        scheduleRefresh();
      }
    };
    reconnectRef.current = () => {
      socket?.close();
      socket = undefined;
      void connect();
    };
    refreshVisibleRef.current = () => {
      markAllVisibleDirty();
      scheduleRefresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", reconnectRef.current);
    const safetyTimer = window.setInterval(
      () => {
        if (!document.hidden && socket?.readyState === WebSocket.OPEN) {
          markAllVisibleDirty();
          scheduleRefresh();
        }
      },
      safetyRefreshMs + Math.random() * 5_000,
    );
    void connect();

    return () => {
      disposed = true;
      socket?.close(1000, "Dashboard unmounted");
      window.clearTimeout(reconnectTimer);
      window.clearTimeout(pollTimer);
      window.clearTimeout(refreshTimer);
      window.clearInterval(safetyTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", reconnectRef.current);
      reconnectRef.current = () => {};
      refreshVisibleRef.current = () => {};
    };
  }, [enabled, firmId, queryClient, userId]);

  useEffect(() => {
    if (enabled && activeTabId) refreshVisibleRef.current();
  }, [activeTabId, enabled, firmId, userId, visibleTableIds]);

  const reconnect = useCallback(() => {
    reconnectRef.current();
  }, []);
  return {
    status,
    ready: status === "ready" || status === "degraded",
    afterSequence,
    reconnect,
  };
};
